import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Alert, AlertDescription } from "../ui/alert";
import { Info, AlertTriangle } from "lucide-react";

interface SubmitExampleConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (isAnonymous: boolean) => void;
  userName?: string;
}

const SubmitExampleConsentDialog = ({
  open,
  onOpenChange,
  onSubmit,
  userName = "You",
}: SubmitExampleConsentDialogProps) => {
  const { t } = useTranslation();
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [removedSensitiveInfo, setRemovedSensitiveInfo] = useState(false);
  const [grantPermission, setGrantPermission] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset state when closing
      setIsAnonymous(false);
      setRemovedSensitiveInfo(false);
      setGrantPermission(false);
    }
    onOpenChange(open);
  };

  const handleSubmit = () => {
    if (removedSensitiveInfo && grantPermission) {
      onSubmit(isAnonymous);
      handleOpenChange(false);
    }
  };

  const canSubmit = removedSensitiveInfo && grantPermission;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("submit_example_for_approval") || "Submit Example for Approval"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("submit_example_description") ||
              "Your example will be reviewed by the activity author before being published."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Anonymity Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t("choose_attribution") || "Choose attribution:"}
            </Label>
            <RadioGroup
              value={isAnonymous ? "anonymous" : "named"}
              onValueChange={(value) => setIsAnonymous(value === "anonymous")}
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="named" id="named" className="mt-1" />
                <div className="flex-1">
                  <Label
                    htmlFor="named"
                    className="text-sm font-medium cursor-pointer"
                  >
                    {t("publish_with_name") || `Publish with my name (${userName})`}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("publish_with_name_description") ||
                      "The author will know you submitted this. You can contact them later for changes or removal."}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem
                  value="anonymous"
                  id="anonymous"
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="anonymous"
                    className="text-sm font-medium cursor-pointer"
                  >
                    {t("publish_anonymously") || "Publish anonymously"}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("publish_anonymously_description") ||
                      "Author won't know who submitted. You cannot reclaim or modify after approval."}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Warning for anonymous submission */}
          {isAnonymous && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <AlertDescription className="text-xs text-yellow-800">
                {t("anonymous_submission_warning") ||
                  "By submitting anonymously, you're giving up this example completely. Once approved, you cannot reclaim it or request changes."}
              </AlertDescription>
            </Alert>
          )}

          {/* Consent Checkboxes */}
          <div className="space-y-3 pt-2 border-t">
            <Label className="text-sm font-medium">
              {t("required_confirmations") || "Required confirmations:"}
            </Label>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="removed-sensitive"
                checked={removedSensitiveInfo}
                onCheckedChange={(checked) =>
                  setRemovedSensitiveInfo(checked === true)
                }
                className="mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="removed-sensitive"
                  className="text-sm cursor-pointer"
                >
                  {t("confirm_removed_sensitive_info") ||
                    "I've removed all sensitive information"}
                </Label>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="grant-permission"
                checked={grantPermission}
                onCheckedChange={(checked) =>
                  setGrantPermission(checked === true)
                }
                className="mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="grant-permission"
                  className="text-sm cursor-pointer"
                >
                  {t("confirm_grant_permission") ||
                    "I grant permission to use my data as an example"}
                </Label>
              </div>
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription className="text-xs">
              {t("submit_example_info") ||
                "Your example will not be visible to others until the activity author reviews and approves it."}
            </AlertDescription>
          </Alert>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("cancel") || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={!canSubmit ? "opacity-50 cursor-not-allowed" : ""}
          >
            {t("submit") || "Submit"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SubmitExampleConsentDialog;
